import React, { useState, useEffect, useCallback } from 'react';
import { reportService, supermercadoService, userService, productService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function StockMovementsReport() {
  const [movements, setMovements] = useState([]);
  const [supermercados, setSupermercados] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  
  const [filters, setFilters] = useState({
    supermercadoId: '',
    userId: '',
    productId: '',
    fromDate: '',
    toDate: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0
  });

  const [productSearch, setProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    const fetchFilterData = async () => {
      // Fetch each filter data independently to avoid one failure breaking all
      try {
        const supResponse = await supermercadoService.getAllSupermercados();
        if (supResponse.success) {
          setSupermercados(supResponse.data || []);
        }
      } catch (err) {
        console.error('Error loading supermercados:', err);
      }
      
      try {
        const userResponse = await userService.getAllUsers();
        if (userResponse.success) {
          setUsers(userResponse.data || []);
        }
      } catch (err) {
        // Users list requires ADMIN_SISTEMA role, so deposito users won't have access
        console.error('Error loading users:', err);
      }
      
      try {
        const prodResponse = await productService.getAllProducts();
        if (prodResponse.success) {
          setProducts(prodResponse.data || []);
        }
      } catch (err) {
        console.error('Error loading products:', err);
      }
    };
    fetchFilterData();
  }, []);

  const fetchMovements = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      
      const filterParams = {
        ...filters,
        page: pagination.page,
        size: 20
      };
      
      // Clean empty values
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });
      
      const response = await reportService.getStockMovementsReport(filterParams);
      if (response.success) {
        const data = response.data;
        setMovements(data.content || []);
        setPagination({
          page: data.pageNumber,
          totalPages: data.totalPages,
          totalElements: data.totalElements
        });
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el reporte de movimientos');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  useEffect(() => {
    if (productSearch.trim() === '') {
      setFilteredProducts([]);
    } else {
      const query = productSearch.toLowerCase();
      const filtered = products.filter(p =>
        p.codigoBarras.toLowerCase().includes(query) ||
        p.nombre.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered.slice(0, 10));
    }
  }, [productSearch, products]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleProductSelect = (product) => {
    setFilters(prev => ({ ...prev, productId: product.id }));
    setProductSearch(product.nombre);
    setFilteredProducts([]);
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      supermercadoId: '',
      userId: '',
      productId: '',
      fromDate: '',
      toDate: ''
    });
    setProductSearch('');
    setPagination({ page: 0, totalPages: 0, totalElements: 0 });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-UY', { timeZone: 'America/Montevideo' });
  };

  const getMovementTypeLabel = (tipo) => {
    switch (tipo) {
      case 'ENTRADA': return 'Entrada';
      case 'SALIDA': return 'Salida';
      case 'ORDEN_ENVIADA': return 'Orden Enviada';
      case 'AJUSTE_MANUAL': return 'Ajuste Manual';
      default: return tipo;
    }
  };

  const getMovementTypeClass = (tipo) => {
    switch (tipo) {
      case 'ENTRADA': return 'movement-entrada';
      case 'SALIDA': return 'movement-salida';
      case 'ORDEN_ENVIADA': return 'movement-orden';
      default: return '';
    }
  };

  return (
    <div className="report-container">
      <h2>Reporte de Movimientos de Stock</h2>
      
      <div className="report-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="supermercadoId">Supermercado</label>
            <select
              id="supermercadoId"
              value={filters.supermercadoId}
              onChange={(e) => handleFilterChange('supermercadoId', e.target.value)}
            >
              <option value="">Todos</option>
              {supermercados.map(sup => (
                <option key={sup.id} value={sup.id}>{sup.nombre}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="userId">Usuario</label>
            <select
              id="userId"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
            >
              <option value="">Todos</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.nombre} ({user.username})</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="productSearch">Producto</label>
            <div className="product-search">
              <input
                type="text"
                id="productSearch"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  if (e.target.value === '') {
                    handleFilterChange('productId', '');
                  }
                }}
                placeholder="Buscar producto..."
              />
              {filteredProducts.length > 0 && (
                <ul className="search-results">
                  {filteredProducts.map(product => (
                    <li key={product.id} onClick={() => handleProductSelect(product)}>
                      <span className="product-code">{product.codigoBarras}</span>
                      <span className="product-name">{product.nombre}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="fromDate">Desde</label>
            <input
              type="date"
              id="fromDate"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="toDate">Hasta</label>
            <input
              type="date"
              id="toDate"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn btn-primary">
            Aplicar Filtros
          </button>
          <button onClick={handleClearFilters} className="btn btn-secondary">
            Limpiar Filtros
          </button>
        </div>
        <div className="export-buttons" style={{ marginTop: '10px' }}>
          <button
            onClick={async () => {
              try {
                await reportService.exportStockMovementsToPdf(filters);
              } catch (err) {
                console.error('Error exporting to PDF:', err);
                alert('Error al exportar a PDF');
              }
            }}
            className="btn btn-small btn-primary"
            style={{ marginRight: '10px' }}
          >
            📄 Exportar a PDF
          </button>
          <button
            onClick={async () => {
              try {
                await reportService.exportStockMovementsToExcel(filters);
              } catch (err) {
                console.error('Error exporting to Excel:', err);
                alert('Error al exportar a Excel');
              }
            }}
            className="btn btn-small btn-success"
            style={{ marginRight: '10px' }}
          >
            📊 Exportar a Excel
          </button>
          <button
            onClick={async () => {
              try {
                await reportService.exportStockMovementsToCsv(filters);
              } catch (err) {
                console.error('Error exporting to CSV:', err);
                alert('Error al exportar a CSV');
              }
            }}
            className="btn btn-small btn-info"
          >
            📋 Exportar a CSV
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />

      {loading ? (
        <p className="loading-text">Cargando movimientos...</p>
      ) : (
        <>
          <div className="report-summary">
            <p>Total de movimientos encontrados: {pagination.totalElements}</p>
          </div>

          {movements.length === 0 ? (
            <div className="empty-state">
              <p>No hay movimientos que coincidan con los criterios de búsqueda</p>
            </div>
          ) : (
            <div className="report-table-container">
              <table className="report-table movements-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Producto</th>
                    <th>Tipo</th>
                    <th>Cantidad</th>
                    <th>Stock Anterior</th>
                    <th>Stock Posterior</th>
                    <th>Supermercado</th>
                    <th>Usuario</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={movement.id} className={getMovementTypeClass(movement.tipo)}>
                      <td>{formatDate(movement.createdAt)}</td>
                      <td>
                        <div className="product-cell">
                          <span className="product-name">{movement.productName}</span>
                          <span className="product-code">{movement.productBarcode}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`movement-badge ${getMovementTypeClass(movement.tipo)}`}>
                          {getMovementTypeLabel(movement.tipo)}
                        </span>
                      </td>
                      <td className="quantity-cell">
                        <span className={movement.tipo === 'ENTRADA' ? 'positive' : 'negative'}>
                          {movement.tipo === 'ENTRADA' ? '+' : '-'}{movement.cantidad}
                        </span>
                      </td>
                      <td>{movement.stockAnterior}</td>
                      <td>{movement.stockPosterior}</td>
                      <td>{movement.supermercadoNombre || '-'}</td>
                      <td>{movement.userName}</td>
                      <td>{movement.descripcion || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="btn btn-small"
              >
                Anterior
              </button>
              <span>Página {pagination.page + 1} de {pagination.totalPages}</span>
              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="btn btn-small"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StockMovementsReport;
